import { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { AxiosError } from 'axios';

import { DisplayToast } from '@/helpers';

import { MutationResponse } from '@/types/api';

import { ResponseValidationError } from '@/network/ResponseValidationError';

type ProcessErrorResponseParams<
  TFieldValues extends FieldValues = FieldValues,
> = {
  error: unknown;
  form: UseFormReturn<TFieldValues>;
  errorFieldMap?: Partial<Record<string, keyof TFieldValues & string>>;
};

type ProcessSuccessResponseParams<
  TFieldValues extends FieldValues = FieldValues,
> = {
  response?: MutationResponse;
  form?: UseFormReturn<TFieldValues>;
  manualRedirectUrl?: string;
  entityIdKey?: string;
};

export const processErrorResponse = <
  TFieldValues extends FieldValues = FieldValues,
>(
  params: ProcessErrorResponseParams<TFieldValues>,
) => {
  const { error, form, errorFieldMap } = params;
  const { setError, getValues } = form;

  if (!error || typeof error !== 'object') {
    return;
  }

  /**
   * An error naming something the form has no input for is shown by nobody: `setError`
   * stores it in RHF state and the submit just fails silently. That covers `base` and any
   * attribute the API validates without rendering it (`system_config` on a payment key).
   * Those go to a toast; everything the form does render stays inline under its field.
   */
  const formFieldNames = new Set(Object.keys(getValues()));

  const displayError = (rawFieldName: string, message: string) => {
    const fieldName = (errorFieldMap?.[rawFieldName] ??
      rawFieldName) as Path<TFieldValues>;
    const rootFieldName = String(fieldName).split(/[.[]/)[0];

    if (!rootFieldName || !formFieldNames.has(rootFieldName)) {
      DisplayToast.error(message);
      return;
    }

    setError(fieldName, { type: 'server', message });
  };

  let responseData: unknown;

  if (ResponseValidationError.isResponseValidationError(error)) {
    responseData = error.response.data;
  } else if ('response' in error) {
    responseData = (error as AxiosError).response?.data;
  } else {
    return;
  }

  if (!responseData || typeof responseData !== 'object') {
    return;
  }

  // `{ error: { message } }` names no field, so it can only ever be a toast — never a
  // silent return, which is the failure mode this branch exists for.
  if (!('errors' in responseData) && 'error' in responseData) {
    const message = (responseData.error as { message?: string } | undefined)
      ?.message;

    DisplayToast.error(message ? String(message) : undefined);

    return;
  }

  if ('errors' in responseData && Array.isArray(responseData.errors)) {
    responseData.errors.forEach((err) => {
      if (
        err &&
        typeof err === 'object' &&
        'field' in err &&
        'message' in err
      ) {
        const rawFieldName = String(err.field)
          .replace(/^\w+\[/, '')
          .replace(/\]$/, '');
        displayError(rawFieldName, String(err.message));
      }
    });
    return;
  }

  if (
    'errors' in responseData &&
    typeof responseData.errors === 'object' &&
    !Array.isArray(responseData.errors)
  ) {
    const errorsObj = responseData.errors as Record<string, string[] | string>;
    Object.entries(errorsObj).forEach(([field, messages]) => {
      const rawFieldName = field.replace(/^\w+\[/, '').replace(/\]$/, '');
      const message = Array.isArray(messages) ? messages[0] : messages;
      displayError(rawFieldName, String(message));
    });
  }
};

export const processSuccessResponse = <
  TFieldValues extends FieldValues = FieldValues,
>(
  params: ProcessSuccessResponseParams<TFieldValues>,
) => {
  const { response, manualRedirectUrl, entityIdKey, form } = params;

  if (response?.redirect) {
    window.location.href = response.redirect;
    return;
  }

  if (
    entityIdKey &&
    response &&
    typeof response === 'object' &&
    entityIdKey in response
  ) {
    const entity = (response as Record<string, { id?: number }>)[entityIdKey];
    if (entity?.id && manualRedirectUrl) {
      window.location.href = `${manualRedirectUrl}/${entity.id}`;
      return;
    }
  }

  if (
    response &&
    'errors' in response &&
    !Array.isArray(response.errors) &&
    form
  ) {
    const { setError } = form;

    const errors = response.errors as Record<string, string[]>;

    Object.keys(errors).forEach((field) => {
      setError(field as Path<TFieldValues>, {
        message: errors[field as keyof typeof errors]?.[0] || '',
      });
    });
    return;
  }

  if (manualRedirectUrl) {
    window.location.href = manualRedirectUrl;
    return;
  }
};
